import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { LoggerService } from '../logger';

/**
 * Native keyboard wiring for iOS / Android.
 *
 * Smoothness story
 * ----------------
 * `dvh` does not interpolate — it snaps to the new viewport when iOS
 * finishes its keyboard animation. To get a continuous lift we drive a
 * CSS custom property `--kb-inset` from the keyboard plugin's
 * `keyboardWillShow` / `keyboardWillHide` events (which fire at the start
 * of the OS animation) and let a CSS `transition` interpolate the height
 * change over the same ~250ms curve iOS uses.
 *
 * Two CSS variables are published on `:root`:
 *   --kb-inset         : keyboard height in px, 0 when closed
 *   --kb-safe-bottom   : the bottom safe-area inset we want chat surfaces
 *                        to subtract. Equal to `env(safe-area-inset-bottom)`
 *                        when the keyboard is closed, and `0px` when it is
 *                        open — because the keyboard itself already covers
 *                        the home-indicator zone, subtracting both would
 *                        leave a visible gap between the input and the
 *                        keyboard top edge.
 *   --kb-transition    : shorthand transition value used by consumers so
 *                        every animated height/inset reads from the same
 *                        timing as the OS keyboard.
 *
 * `KeyboardResize.None` is used so the WebView frame stays full-height —
 * only our CSS vars animate, which means one continuous animation with no
 * `dvh` snap to fight.
 *
 * Web (non-native) is a no-op — browsers handle this natively.
 *
 * Mount this hook once near the root of the authenticated app
 * (BhabiAppLayoutWithoutCtx). Safe to mount multiple times.
 */
export const useNativeKeyboardBehavior = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const cleanups: Array<() => void> = [];
    const root = document.documentElement;

    // Initial values without a transition so the first paint doesn't lift
    // anything from 0.
    root.style.setProperty('--kb-inset', '0px');
    root.style.setProperty(
      '--kb-safe-bottom',
      'env(safe-area-inset-bottom)'
    );
    root.style.setProperty('--kb-transition', '0ms linear');

    (async () => {
      try {
        // Don't let the plugin touch layout — we drive everything via
        // --kb-inset / --kb-safe-bottom below.
        await Keyboard.setResizeMode({ mode: KeyboardResize.None });
        if (Capacitor.getPlatform() === 'ios') {
          await Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(
            () => {}
          );
          await Keyboard.setScroll({ isDisabled: true }).catch(() => {});
        }
      } catch (err) {
        LoggerService.warn?.('useNativeKeyboardBehavior setup failed', err);
      }
      if (cancelled) return;

      // iOS animates the keyboard with a ~250ms ease-out curve.
      const KEYBOARD_EASE = 'cubic-bezier(0.17, 0.59, 0.4, 0.77)';
      const KEYBOARD_DURATION_MS = 250;

      try {
        const showHandle = await Keyboard.addListener(
          'keyboardWillShow',
          info => {
            const h = Math.max(0, info?.keyboardHeight ?? 0);
            root.style.setProperty(
              '--kb-transition',
              `${KEYBOARD_DURATION_MS}ms ${KEYBOARD_EASE}`
            );
            root.style.setProperty('--kb-inset', `${h}px`);
            // The keyboard already covers the home-indicator zone — collapse
            // the safe-area inset so chat inputs sit flush against the
            // keyboard top edge with no dead band in between.
            root.style.setProperty('--kb-safe-bottom', '0px');
          }
        );
        cleanups.push(() => {
          showHandle.remove().catch(() => {});
        });

        const hideHandle = await Keyboard.addListener(
          'keyboardWillHide',
          () => {
            root.style.setProperty(
              '--kb-transition',
              `${KEYBOARD_DURATION_MS}ms ${KEYBOARD_EASE}`
            );
            root.style.setProperty('--kb-inset', '0px');
            root.style.setProperty(
              '--kb-safe-bottom',
              'env(safe-area-inset-bottom)'
            );
          }
        );
        cleanups.push(() => {
          hideHandle.remove().catch(() => {});
        });
      } catch (err) {
        LoggerService.warn?.(
          'useNativeKeyboardBehavior: keyboard listeners failed',
          err
        );
      }
    })();

    return () => {
      cancelled = true;
      cleanups.forEach(fn => {
        try {
          fn();
        } catch {
          /* ignore */
        }
      });
      root.style.removeProperty('--kb-inset');
      root.style.removeProperty('--kb-safe-bottom');
      root.style.removeProperty('--kb-transition');
    };
  }, []);
};
